// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — games in a meeting (`docs/AYNI_XR_MEETING_GAMES_PLAN.md`), on
 * screen. Only in a meeting whose host's plan lends games (Education,
 * decision D3; the room checks it too).
 *
 *   - **The presenter**: a "Games" pill beside the activities' pill opens a
 *     sheet — trivia from the Khipu open in the player (one round per quiz
 *     step), or Maypi, "where on Earth?" (place rounds from AYNI 3D's
 *     hand-checked places, and data rounds on the dataset shown) — with the
 *     answer time and the number of rounds. Then the game panel: the
 *     tables (a player can be moved before the first round), each round
 *     with its right answer and how many have answered, the results and
 *     the board, Next round, End. This page builds the rounds and, for a
 *     data round, scores the pins from its own frame; the room referees.
 *     Or a table game, AYNI 3D's Wayra's Adventures or Team Earth: one game
 *     per table, all started at once, each table at its own pace.
 *   - **Everyone else**: the same panel, as a player — the funny name the
 *     room gave them (one change), their table and who sits there, the
 *     round to answer (options, or a tap on the globe, which is theirs to
 *     turn during a place round), the results and their place on the
 *     board. Nobody types anything, so no name of a child reaches a screen.
 *
 * In a headset the game is in the Khipu panel (`gameVrState`): a trivia
 * round's answers are its buttons, a place round is answered by tapping the
 * globe (`gameVrTap`), and a table game is shown, to play on a phone or a
 * computer. A phone in AR shows the page's panel over the camera
 * (`xrPhoneChrome` adopts it).
 *
 * The panel wears the lesson panel's frame (a side panel on a desktop that
 * pushes the globe, `--panel-push-game`; a card at the foot on a phone).
 * Lives in `src/ui/` so `check:i18n-strings` covers it.
 */

import { getLocale, t } from '../i18n'
import type { ActivityGlobe } from '../services/activityOverlay'
import { renderGameName, type GameName } from '../services/gameNames'
import { dataRound, placeRounds, sampleField, scoreDataPins, triviaRounds, type TriviaRound, type ValueProbe } from '../services/gameRounds'
import type { RoomStatus } from '../services/roomClient'
import { distanceKm } from '../services/roomActivity'
import type { RoomClientMessage } from '../services/roomProtocol'
import { GAME_MAX_PLAYERS, ROUND_DURATIONS_S, isTableKind, newGameKey, type GameKind, type GameView, type RoundSpec } from '../services/roomGame'
import type { TableAction } from '../services/gameTables'
import { tableHtml, tablesHtml, wayraMarks } from './tableGamesUI'
import { localized, newKhipuId, type Khipu } from '../services/khipu'
import { buildGameKhipu, type GameLogRound } from '../services/gameKhipu'
import { saveKhipu } from '../services/khipuStore'
import { zipKhipu } from '../services/lessonService'
import { formatDate } from '../i18n/format'
import { escapeAttr, escapeHtml } from './domUtils'
import type { VrKhipuState } from '../services/vrKhipuPanel'
import { announceSidePanel, leaveSidePanel } from './floatingPanel'

export const GAME_PANEL_ID = 'game-panel'

/** The room, as far as games need it. */
export interface GamesRoom {
  onStatus(listener: (status: RoomStatus) => void): () => void
  send(message: Exclude<RoomClientMessage, { t: 'state' }>): void
}

/** What the presenter's page lends the games. */
export interface GamesHost {
  /** The Khipu open in the player, if any. */
  openKhipu(): Khipu | null
  /** Put step `index` of the open Khipu on the globe (the room follows). */
  showKhipuStep(index: number): void
  /** Play the open Khipu as a game (its quizzes are the rounds), or as itself again. */
  setKhipuGame(on: boolean): void
  /** The dataset on the main globe as values (a value-encoded one), else null. */
  dataProbe(): { title: string; probe: ValueProbe } | null
  /** Turn the presenter's globe to a place (the room follows). */
  flyTo(point: [number, number]): void
  /** The dataset on the presenter's main globe, for the game's Khipu. */
  datasetId(): string | null
  /** A dataset's title, for the Khipu's dataset list. */
  titleOf(datasetId: string): string | null
  /** Whether this account keeps Khipus, and whether downloads are the exchange copy. */
  canKeep(): boolean
  exchangeOnly(): boolean
}

/** Rounds of Maypi the sheet offers. */
const MAYPI_ROUNDS = [5, 8, 10] as const
/** A table game's choices the sheet offers. */
const WAYRA_STOPS = [3, 5] as const
const WAYRA_CLUE_S = [15, 20, 30] as const
const EARTH_ROUNDS = [3, 5] as const
const EARTH_PLAN_S = [45, 60, 90] as const
/** How often a player at a table game tells the room they are still there (the engines count a still seat away after 45 s). */
const TOUCH_EVERY_MS = 20_000

/** What the presenter's tab keeps of a game it runs: the rounds it built, and the next one. */
interface Driver {
  gameId: string | null
  kind: GameKind
  rounds: { spec: RoundSpec; step?: number; high?: boolean }[]
  next: number
  /** The open round's own facts (a data round's direction), by the room's round id. */
  scored: string | null
  flown: string | null
  /** The rounds revealed so far, for the game's Khipu (totals only, never who answered what). */
  log?: GameLogRound[]
}

const DRIVER_KEY = (code: string): string => `ayni-game-driver:${code}`
const PLAYER_KEY = (code: string): string => `ayni-game-key:${code}`

function store(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** A fresh secret from the browser's random source. */
function freshKey(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  let i = 0
  return newGameKey(() => bytes[i++ % bytes.length] / 256)
}

/** This tab's player secret in meeting `code`: made once, kept in the tab. */
export function playerKeyFor(code: string): string {
  const s = store()
  const kept = s?.getItem(PLAYER_KEY(code))
  if (kept) return kept
  const key = freshKey()
  try {
    s?.setItem(PLAYER_KEY(code), key)
  } catch {
    // This page only.
  }
  return key
}

/** Seconds left on a round, on this device's clock. */
export function secondsLeft(view: GameView, receivedAt: number, now: number = Date.now()): number {
  const r = view.round
  if (!r || r.closed) return 0
  return Math.max(0, Math.ceil((r.remainingMs - (now - receivedAt)) / 1000))
}

const number = (n: number): string => n.toLocaleString(getLocale())
const nameOf = (n: GameName): string => renderGameName(n, getLocale())

let latestView: GameView | null = null
/** The mounted games' room and status, for the headset (`gameVrState`, `gameVrAnswer`, `gameVrTap`). */
let vrLink: { status: () => RoomStatus | null; send: GamesRoom['send'] } | null = null
let vrCache: { key: string; state: VrKhipuState } | null = null

/**
 * The game in the headset's Khipu panel, for a player; null when there is
 * no game or this page is not a player (the presenter runs it from the page).
 * The same object until something shown changed.
 */
export function gameVrState(now: number = Date.now()): VrKhipuState | null {
  const status = vrLink?.status() ?? null
  const v = status?.game ?? null
  if (!v || v.teacher || !v.you || status?.grants?.games !== true) return null
  const r = v.round
  const lines: string[] = [t('game.you.isPlain', { name: nameOf(v.you.name), table: v.you.table + 1 })]
  let quiz: VrKhipuState['quiz']
  if (isTableKind(v.kind)) {
    lines.push(t('game.vr.tables'))
    if (v.tableResult) lines.push(t(v.kind === 'wayra' ? 'game.tables.treats' : 'game.stars', { n: v.tableResult.score, of: 4 }))
  } else if (v.phase === 'lobby') {
    lines.push(t('game.lobby.wait'))
  } else if (r) {
    if (!r.closed) lines.push(t('game.round.left', { s: secondsLeft(v, status?.gameAt ?? now, now) }))
    if (r.kind === 'choice' && r.options) {
      // Four buttons at most in the headset; more options are answered on the page.
      if (r.options.length <= 4 && r.playing) quiz = { options: r.options, chosen: r.choice, correct: r.result?.correct ?? -1 }
      else lines.push(...r.options.map((o, i) => `${'ABCDEF'[i]}. ${o}`))
    } else if (!r.closed) {
      // The headset's own words (a trigger, not a tap): this panel is drawn in headsets only (phones show the page's card).
      lines.push(t(r.pin ? 'game.vr.placed' : 'game.vr.tap'))
    }
    if (r.result) {
      if (r.result.label) lines.push(t('game.point.answer', { place: r.result.label }))
      lines.push(r.result.points === null || (r.choice === null && r.pin === null) ? t('game.round.missed') : t('game.round.points', { n: number(r.result.points) }))
    }
  }
  if (v.board?.you) lines.push(t('game.board.you', { place: v.board.you.place, total: v.players, score: number(v.board.you.score) }))
  const state: VrKhipuState = {
    title: v.title,
    progress: v.rounds > 0 ? t('game.round.n', { n: v.rounds }) : t('game.lobby.title'),
    heading: r?.prompt ?? '',
    dataset: '',
    lines,
    media: 0,
    canPrev: false,
    isLast: false,
    offersPlay: false,
    playing: false,
    readOnly: true,
    ...(quiz ? { quiz } : {}),
  }
  const key = JSON.stringify(state)
  if (vrCache?.key === key) return vrCache.state
  vrCache = { key, state }
  return state
}

/** A press in the headset's Khipu panel: a trivia answer. Returns whether the game took it. */
export function gameVrAnswer(action: string): boolean {
  const status = vrLink?.status() ?? null
  const r = status?.game?.round
  if (!vrLink || !r || !gameVrState() || !action.startsWith('quiz-')) return false
  if (r.kind === 'choice' && !r.closed && r.playing && r.choice === null) vrLink.send({ t: 'gans', id: r.id, choice: Number(action.slice(5)), p: null })
  return true
}

/** A tap on the headset's globe: a place round's answer. Returns whether the game took it. */
export function gameVrTap(lat: number, lon: number): boolean {
  const status = vrLink?.status() ?? null
  const v = status?.game
  const r = v?.round
  if (!vrLink || !v || v.teacher || !r || r.kind !== 'point' || r.closed || !r.playing) return false
  vrLink.send({ t: 'gans', id: r.id, choice: null, p: [lat, lon] })
  return true
}

/** A player's game name from the connection id (for a raised hand while a game is on); null when not a player. */
export function gameNameOfConn(conn: string): string | null {
  const p = latestView?.teacher?.players.find((q) => q.conn === conn)
  return p ? nameOf(p.name) : null
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

function button(className: string, text: string): HTMLButtonElement {
  const b = el('button', className, text)
  b.type = 'button'
  // Taps on the overlay must not reach the AR session as a select.
  b.addEventListener('beforexrselect', (ev) => ev.preventDefault())
  return b
}

function boardHtml(view: GameView, final: boolean): string {
  const b = view.board
  if (!b) return ''
  const rows = b.top
    .map((r, i) => `<li class="game-board-row${r.you ? ' game-you' : ''}"><span class="game-board-place">${i + 1}</span><span class="game-board-name">${escapeHtml(nameOf(r.name))}</span><span class="game-board-score">${escapeHtml(number(r.score))}</span></li>`)
    .join('')
  const tables = b.tables
    .map((tb, i) => `<li class="game-board-row${view.you && view.you.table + 1 === tb.n ? ' game-you' : ''}"><span class="game-board-place">${i + 1}</span><span class="game-board-name">${escapeHtml(t('game.table', { n: tb.n }))} <span class="game-muted">(${escapeHtml(t('game.board.size', { n: tb.size }))})</span></span><span class="game-board-score">${escapeHtml(number(tb.score))}</span></li>`)
    .join('')
  // A table game is played together: the tables are ranked, and a player's place is their table's.
  const together = isTableKind(view.kind)
  const you = b.you
    ? `<p class="game-you-line">${escapeHtml(together
      ? t('game.board.yourTable', { place: b.you.place, total: b.tables.length })
      : t('game.board.you', { place: b.you.place, total: view.players, score: number(b.you.score) }))}</p>`
    : ''
  return `<div class="game-board">
    <h3 class="lesson-step-title">${escapeHtml(t(final ? 'game.board.final' : 'game.board.title'))}</h3>
    ${you}
    ${together ? '' : `<h4 class="game-board-head">${escapeHtml(t('game.board.players'))}</h4>
    <ol class="game-board-list">${rows || `<li class="game-muted">${escapeHtml(t('game.board.empty'))}</li>`}</ol>`}
    ${tables ? `<h4 class="game-board-head">${escapeHtml(t('game.board.tables'))}</h4><ol class="game-board-list">${tables}</ol>` : ''}
  </div>`
}

/** Mount the games for this page. Returns the teardown. */
export function mountMeetingGames(root: HTMLElement, room: GamesRoom, globe: ActivityGlobe, host: GamesHost, opts: { code: string }): () => void {
  let status: RoomStatus | null = null
  let connectEpoch = 0
  let joinSent = ''
  let shownRound: string | null = null
  let sheetOpen = false
  let pickedKind: GameKind = 'trivia'
  let durationS: number = 30
  let maypiRounds: number = 8
  let withData = true
  let wayraStops: 3 | 5 = 3
  let clueS: 15 | 20 | 30 = 20
  let earthRounds: 3 | 5 = 3
  let planS: 45 | 60 | 90 = 60
  let homeName = t('game.wayra.homeDefault')
  let driver: Driver | null = loadDriver()

  function loadDriver(): Driver | null {
    try {
      const raw = store()?.getItem(DRIVER_KEY(opts.code))
      return raw ? (JSON.parse(raw) as Driver) : null
    } catch {
      return null
    }
  }
  function saveDriver(): void {
    try {
      if (driver) store()?.setItem(DRIVER_KEY(opts.code), JSON.stringify(driver))
      else store()?.removeItem(DRIVER_KEY(opts.code))
    } catch {
      // This page only.
    }
  }

  const presenter = (): boolean => !!status?.meeting && status.seat === 'presenter'
  const allowed = (): boolean => !!status?.connected && !!status.meeting && status.grants?.games === true

  // ------------------------------------------------------------ the pill
  const pill = button('activity-start game-pill hidden', t('game.pill'))
  pill.setAttribute('aria-haspopup', 'dialog')
  // Beside the activities' pills, in their row (meetingActivitiesUI), or on its own.
  const row = root.querySelector('.activity-row')
  ;(row ?? root).append(pill)

  // ------------------------------------------------------------ the sheet
  const sheet = el('div', 'activity-sheet game-sheet hidden')
  sheet.setAttribute('role', 'dialog')
  sheet.setAttribute('aria-label', t('game.sheet.title'))
  root.append(sheet)

  function renderSheet(): void {
    const k = host.openKhipu()
    const trivia = k ? triviaRounds(k, durationS * 1000, getLocale()) : []
    const data = host.dataProbe()
    if (pickedKind === 'trivia' && !trivia.length) pickedKind = 'maypi'
    const kindButton = (kind: GameKind, title: string, hint: string, disabled = false): string =>
      `<button type="button" class="activity-kind" role="radio" data-kind="${kind}" aria-checked="${String(pickedKind === kind)}"${disabled ? ' disabled' : ''}><strong>${escapeHtml(title)}</strong><span>${escapeHtml(hint)}</span></button>`
    const chips = (name: string, values: readonly number[], current: number, label: (v: number) => string): string =>
      values.map((v) => `<button type="button" class="activity-chip" data-${name}="${v}" aria-pressed="${String(current === v)}">${escapeHtml(label(v))}</button>`).join('')
    sheet.innerHTML = `
      <div class="activity-sheet-header"><h2>${escapeHtml(t('game.sheet.title'))}</h2><button type="button" class="activity-sheet-close" aria-label="${escapeAttr(t('game.sheet.close'))}">✕</button></div>
      <div class="activity-kinds" role="radiogroup">
        ${kindButton('trivia', t('game.kind.trivia'), trivia.length ? t('game.kind.trivia.hint', { title: k ? localized(k.title, getLocale()) : '', n: trivia.length }) : t('game.kind.trivia.none'), !trivia.length)}
        ${kindButton('maypi', t('game.kind.maypi'), t('game.kind.maypi.hint'))}
        ${kindButton('wayra', t('game.kind.wayra'), t('game.kind.wayra.hint'))}
        ${kindButton('earth', t('game.kind.earth'), t('game.kind.earth.hint'))}
      </div>
      ${pickedKind === 'maypi' ? `<div class="activity-field"><span>${escapeHtml(t('game.sheet.rounds'))}</span><div class="activity-chips">${chips('rounds', MAYPI_ROUNDS, maypiRounds, (v) => String(v))}</div></div>
        ${data ? `<label class="game-check"><input type="checkbox" class="game-with-data"${withData ? ' checked' : ''}> ${escapeHtml(t('game.sheet.withData', { title: data.title }))}</label>` : `<p class="lesson-notice">${escapeHtml(t('game.sheet.noData'))}</p>`}` : ''}
      ${pickedKind === 'wayra' ? `<div class="activity-field"><span>${escapeHtml(t('game.sheet.stops'))}</span><div class="activity-chips">${chips('stops', WAYRA_STOPS, wayraStops, (v) => String(v))}</div></div>
        <div class="activity-field"><span>${escapeHtml(t('game.sheet.clueEvery'))}</span><div class="activity-chips">${chips('clue', WAYRA_CLUE_S, clueS, (v) => t('game.sheet.seconds', { n: v }))}</div></div>
        <label class="activity-field"><span>${escapeHtml(t('game.sheet.home'))}</span><input type="text" class="game-home" maxlength="60" value="${escapeAttr(homeName)}"></label>
        <p class="lesson-notice">${escapeHtml(t('game.sheet.homeHint'))}</p>` : ''}
      ${pickedKind === 'earth' ? `<div class="activity-field"><span>${escapeHtml(t('game.sheet.decades'))}</span><div class="activity-chips">${chips('decades', EARTH_ROUNDS, earthRounds, (v) => String(v))}</div></div>
        <div class="activity-field"><span>${escapeHtml(t('game.sheet.planTime'))}</span><div class="activity-chips">${chips('plan', EARTH_PLAN_S, planS, (v) => t('game.sheet.seconds', { n: v }))}</div></div>` : ''}
      ${isTableKind(pickedKind) ? '' : `<div class="activity-field"><span>${escapeHtml(t('game.sheet.time'))}</span><div class="activity-chips">${chips('secs', ROUND_DURATIONS_S, durationS, (v) => t('game.sheet.seconds', { n: v }))}</div></div>`}
      <p class="lesson-notice">${escapeHtml(t('game.sheet.privacy'))}</p>
      <div class="activity-actions"><button type="button" class="activity-primary game-start"${pickedKind === 'trivia' && !trivia.length ? ' disabled' : ''}>${escapeHtml(t('game.sheet.start'))}</button></div>`
  }

  function openSheet(on: boolean): void {
    sheetOpen = on
    if (on) {
      if (!host.openKhipu()) pickedKind = 'maypi'
      renderSheet()
    }
    sheet.classList.toggle('hidden', !on)
    if (on) sheet.querySelector<HTMLElement>('.activity-kind[aria-checked="true"]')?.focus()
  }

  function startGame(): void {
    const lang = getLocale()
    const ms = durationS * 1000
    if (pickedKind === 'trivia') {
      const k = host.openKhipu()
      const rounds: TriviaRound[] = k ? triviaRounds(k, ms, lang) : []
      if (!k || !rounds.length) return
      const title = localized(k.title, lang) || t('game.kind.trivia')
      driver = { gameId: null, kind: 'trivia', rounds: rounds.map((r) => ({ spec: r.spec, step: r.step })), next: 0, scored: null, flown: null }
      host.setKhipuGame(true)
      room.send({ t: 'game', g: { kind: 'trivia', title: t('game.title.trivia', { title }) } })
    } else if (pickedKind === 'wayra' || pickedKind === 'earth') {
      const kind = pickedKind
      // Wayra's home: where the presenter's globe looks now, under the name given.
      const at = globe.center()
      const name = homeName.trim().slice(0, 60) || t('game.wayra.homeDefault')
      const options = kind === 'wayra'
        ? { stops: wayraStops, clueSeconds: clueS, ...(at ? { home: { name, lat: at[0], lon: at[1] } } : {}) }
        : { rounds: earthRounds, planSeconds: planS }
      driver = { gameId: null, kind, rounds: [], next: 0, scored: null, flown: null }
      room.send({ t: 'game', g: { kind, title: t(kind === 'wayra' ? 'game.kind.wayra' : 'game.kind.earth'), options } })
    } else {
      const data = withData ? host.dataProbe() : null
      const dataCount = data ? Math.max(1, Math.round(maypiRounds / 3)) : 0
      const places = placeRounds(maypiRounds - dataCount, ms, lang, { where: t('game.maypi.where') })
      const rounds: Driver['rounds'] = places.map((spec) => ({ spec }))
      // Every third round, a data round: highest, then lowest, in turn.
      for (let i = 0; i < dataCount; i++) {
        const high = i % 2 === 0
        const spec = dataRound(t(high ? 'game.maypi.data.high' : 'game.maypi.data.low', { title: data!.title }), ms)
        rounds.splice(Math.min(rounds.length, 2 + i * 3), 0, { spec, high })
      }
      driver = { gameId: null, kind: 'maypi', rounds, next: 0, scored: null, flown: null }
      room.send({ t: 'game', g: { kind: 'maypi', title: t('game.title.maypi') } })
    }
    saveDriver()
    openSheet(false)
  }

  sheet.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('button, input')
    if (!target) return
    if (target.classList.contains('activity-sheet-close')) return openSheet(false)
    if (target.classList.contains('game-start')) return startGame()
    if (target instanceof HTMLInputElement && target.classList.contains('game-with-data')) {
      withData = target.checked
      return
    }
    if (target instanceof HTMLInputElement) return
    const b = target as HTMLElement
    if (b.dataset.kind) pickedKind = b.dataset.kind as GameKind
    else if (b.dataset.rounds) maypiRounds = Number(b.dataset.rounds)
    else if (b.dataset.secs) durationS = Number(b.dataset.secs)
    else if (b.dataset.stops) wayraStops = Number(b.dataset.stops) as 3 | 5
    else if (b.dataset.clue) clueS = Number(b.dataset.clue) as 15 | 20 | 30
    else if (b.dataset.decades) earthRounds = Number(b.dataset.decades) as 3 | 5
    else if (b.dataset.plan) planS = Number(b.dataset.plan) as 45 | 60 | 90
    else return
    renderSheet()
  })
  sheet.addEventListener('input', (e) => {
    const input = e.target as HTMLInputElement
    if (input.classList.contains('game-home')) homeName = input.value
  })
  sheet.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      openSheet(false)
      pill.focus()
    }
  })
  pill.addEventListener('click', () => openSheet(!sheetOpen))

  // ------------------------------------------------------------ the panel
  let panel: HTMLElement | null = null

  function ensurePanel(): HTMLElement {
    if (panel?.isConnected && panel.id === GAME_PANEL_ID) return panel
    panel = el('section', 'lesson-player game-panel')
    panel.id = GAME_PANEL_ID
    panel.setAttribute('aria-label', t('game.panel.aria'))
    panel.addEventListener('click', onPanelClick)
    panel.addEventListener('change', onPanelChange)
    document.body.appendChild(panel)
    announceSidePanel()
    return panel
  }

  function closePanel(): void {
    if (panel?.isConnected) leaveSidePanel(panel)
    panel = null
  }

  /** What the last Khipu action said (downloaded, saved, refused). */
  let khipuNote = ''

  function gameKhipu(): Khipu | null {
    const v = status?.game
    if (!v || !driver?.log?.length) return null
    const lang = getLocale()
    return buildGameKhipu(driver.log, {
      id: newKhipuId(),
      title: v.title,
      lang,
      now: Date.now(),
      titleOf: (id) => host.titleOf(id),
      words: {
        round: (n) => t('game.round.n', { n }),
        counts: (list) => t('game.khipu.counts', { list }),
        place: (label, pins) => t('game.khipu.place', { place: label, n: pins }),
        pins: (n) => t('game.point.pins', { n }),
      },
    })
  }

  async function downloadGameKhipu(): Promise<void> {
    const k = gameKhipu()
    if (!k) return
    const { blob, filename } = await zipKhipu(k, [], { exchange: host.exchangeOnly() })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    document.body.append(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
    khipuNote = t('activity.khipu.downloaded', { filename })
    renderPanel()
  }

  async function saveGameKhipu(): Promise<void> {
    const k = gameKhipu()
    if (!k) return
    khipuNote = t('lessons.player.saving')
    renderPanel()
    const result = await saveKhipu(k)
    khipuNote = result.ok
      ? t('lessons.player.saved', { date: formatDate(new Date(result.value.keptUntil), { dateStyle: 'medium' }) })
      : t(result.error === 'too_many' ? 'lessons.player.saveTooMany' : result.error === 'full' ? 'lessons.player.saveFull' : result.error === 'not_configured' ? 'lessons.player.saveUnavailable' : 'lessons.player.saveFailed')
    renderPanel()
  }

  /** The presenter's next step: the next round, or the final board. */
  function nextRound(): void {
    const v = status?.game
    if (!driver || !v) return
    const r = driver.rounds[driver.next]
    if (!r) {
      room.send({ t: 'gctl', a: 'end' })
      return
    }
    driver.next++
    saveDriver()
    if (r.step !== undefined) host.showKhipuStep(r.step)
    room.send({ t: 'ground', r: r.spec })
  }

  function onPanelClick(e: MouseEvent): void {
    const b = (e.target as HTMLElement).closest('button')
    if (!b) return
    const v = status?.game
    if (!v) return
    if (b.classList.contains('game-next')) return nextRound()
    if (b.classList.contains('game-close-round')) return room.send({ t: 'gctl', a: 'close' })
    if (b.classList.contains('game-end')) return room.send({ t: 'gctl', a: 'end' })
    if (b.classList.contains('game-remove')) return room.send({ t: 'gctl', a: 'remove' })
    if (b.classList.contains('game-khipu-download')) return void downloadGameKhipu()
    if (b.classList.contains('game-khipu-save')) return void saveGameKhipu()
    if (b.classList.contains('game-reroll')) return room.send({ t: 'greroll' })
    if (b.classList.contains('game-start-tables')) return room.send({ t: 'gctl', a: 'start' })
    // A table game: the player's action, or a look at a place on their own globe.
    if (b.dataset.look) {
      const [lat, lon] = b.dataset.look.split(',').map(Number)
      if (Number.isFinite(lat) && Number.isFinite(lon)) globe.flyTo([lat, lon])
      return
    }
    if (b.dataset.gplay) {
      const k = b.dataset.gplay
      const a: TableAction | null =
        k === 'vote' ? { k: 'vote', place: b.getAttribute('aria-pressed') === 'true' ? null : b.dataset.place ?? null }
          : k === 'share' ? { k: 'share', yes: b.dataset.yes === '1' }
            : k === 'card' && b.dataset.id ? { k: 'card', id: b.dataset.id }
              : k === 'lens' && b.dataset.role ? { k: 'lens', role: b.dataset.role as Extract<TableAction, { k: 'lens' }>['role'] }
                : k === 'clue' || k === 'next' || k === 'ready' ? { k }
                  : null
      if (a) room.send({ t: 'gplay', a })
      return
    }
    if (b.dataset.option !== undefined && v.round && !v.round.closed && v.round.playing) {
      room.send({ t: 'gans', id: v.round.id, choice: Number(b.dataset.option), p: null })
    }
  }

  function onPanelChange(e: Event): void {
    const s = e.target as HTMLSelectElement
    if (!s.classList.contains('game-move') || !s.dataset.pid) return
    room.send({ t: 'gmove', pid: s.dataset.pid, table: Number(s.value) })
  }

  function teacherLobby(v: GameView): string {
    const players = v.teacher?.players ?? []
    if (!players.length) return `<p class="lesson-notice">${escapeHtml(t('game.lobby.waiting'))}</p>`
    const tables = Math.max(1, v.tables)
    const canMove = v.rounds === 0
    let html = `<p>${escapeHtml(t('game.lobby.count', { n: players.length, tables }))}</p>`
    for (let i = 0; i < tables; i++) {
      const at = players.filter((p) => p.table === i)
      html += `<div class="game-table"><h4 class="game-board-head">${escapeHtml(t('game.table', { n: i + 1 }))}</h4><ul class="game-table-list">${at
        .map((p) => {
          const options = Array.from({ length: tables + 1 }, (_, n) => `<option value="${n}"${n === i ? ' selected' : ''}>${escapeHtml(t('game.table', { n: n + 1 }))}</option>`).join('')
          return `<li><span class="${p.conn ? '' : 'game-muted'}">${escapeHtml(nameOf(p.name))}</span>${canMove ? ` <select class="game-move" data-pid="${escapeAttr(p.pid)}" aria-label="${escapeAttr(t('game.lobby.move', { name: nameOf(p.name) }))}">${options}</select>` : ''}</li>`
        })
        .join('')}</ul></div>`
    }
    return html
  }

  function roundHtml(v: GameView, now: number): string {
    const r = v.round
    if (!r) return ''
    const teacher = !!v.teacher
    const left = secondsLeft(v, status?.gameAt ?? now, now)
    let html = `<p class="game-prompt">${escapeHtml(r.prompt)}</p>`
    if (r.kind === 'choice' && r.options) {
      const counts = r.result?.counts ?? v.teacher?.counts ?? null
      const correct = r.result?.correct ?? (teacher ? driverCorrect() : null)
      html += `<ol class="khipu-quiz-options game-options">${r.options
        .map((o, i) => {
          const state = correct === i && (teacher || r.result) ? ' khipu-quiz-right' : r.result && r.choice === i ? ' khipu-quiz-wrong' : ''
          const can = !teacher && r.playing && !r.closed
          const count = counts && (teacher || r.result) ? ` <span class="game-count">${escapeHtml(String(counts[i] ?? 0))}</span>` : ''
          return `<li><button type="button" class="khipu-quiz-option${state}" data-option="${i}" aria-pressed="${String(r.choice === i)}"${can ? '' : ' disabled'}><span class="khipu-quiz-letter" aria-hidden="true">${'ABCDEF'[i]}</span> ${escapeHtml(o)}${count}</button></li>`
        })
        .join('')}</ol>`
    } else if (!r.closed) {
      html += `<p class="lesson-notice">${escapeHtml(t(teacher ? 'game.point.teacher' : r.pin ? 'game.point.placed' : 'game.point.tap'))}</p>`
    }
    if (!r.closed) {
      html += `<p class="game-timer${left <= 5 ? ' game-urgent' : ''}" data-timer="1" aria-live="off">${escapeHtml(t('game.round.left', { s: left }))}</p>`
      if (teacher) html += `<p>${escapeHtml(t('game.round.answered', { n: v.teacher?.answered ?? 0, of: v.teacher?.eligible ?? 0 }))}</p>`
      else if (!r.playing) html += `<p class="lesson-notice">${escapeHtml(t('game.round.late'))}</p>`
    } else if (r.scoring) {
      html += `<p class="lesson-notice">${escapeHtml(t('game.round.scoring'))}</p>`
    }
    const res = r.result
    if (res) {
      if (r.kind === 'point') {
        if (res.label) html += `<p class="game-answer">${escapeHtml(t('game.point.answer', { place: res.label }))}</p>`
        if (!teacher && r.pin && res.answer) html += `<p>${escapeHtml(t('game.point.distance', { km: number(Math.round(distanceKm(r.pin, res.answer))) }))}</p>`
        if (res.fact) html += `<p class="lesson-notice">${escapeHtml(res.fact)}</p>`
        if (teacher || !r.pin) html += `<p class="lesson-notice">${escapeHtml(t('game.point.pins', { n: res.pins?.length ?? 0 }))}</p>`
      }
      if (!teacher) {
        // Not answered (left out, or here and silent): no answer, whatever it scored.
        if (res.points === null || (r.choice === null && r.pin === null)) html += `<p class="lesson-notice">${escapeHtml(t('game.round.missed'))}</p>`
        else html += `<p class="game-points">${escapeHtml(t('game.round.points', { n: number(res.points) }))}${res.table !== null ? ` · ${escapeHtml(t('game.round.tablePoints', { n: number(res.table) }))}` : ''}</p>`
      }
    }
    return html
  }

  /** The presenter knows the open choice round's answer: the round this tab sent last. */
  function driverCorrect(): number | null {
    const sent = driver?.rounds[(driver?.next ?? 1) - 1]?.spec
    return sent?.kind === 'choice' ? sent.correct : null
  }

  function renderPanel(): void {
    const v = status?.game ?? null
    if (!v || !allowed()) {
      closePanel()
      return
    }
    const p = ensurePanel()
    const now = Date.now()
    const teacher = !!v.teacher
    const runs = presenter()
    const kind = t(v.kind === 'trivia' ? 'game.kind.trivia' : v.kind === 'maypi' ? 'game.kind.maypi' : v.kind === 'wayra' ? 'game.kind.wayra' : 'game.kind.earth')
    const total = runs && driver ? driver.rounds.length : null
    const progress = isTableKind(v.kind)
      ? t(v.phase === 'lobby' ? 'game.lobby.title' : v.phase === 'over' ? 'game.tables.over' : 'game.tables.playing')
      : v.rounds > 0 ? (total ? t('game.round.ofTotal', { n: v.rounds, total }) : t('game.round.n', { n: v.rounds })) : t('game.lobby.title')
    let body = ''
    if (!teacher) {
      if (v.you) {
        body += `<p class="game-you-line">${t('game.you.is', { name: `<strong>${escapeHtml(nameOf(v.you.name))}</strong>`, table: escapeHtml(String(v.you.table + 1)) })}</p>`
        if (v.mates.length) body += `<p class="lesson-notice">${escapeHtml(t('game.you.mates', { list: v.mates.map(nameOf).join(' · ') }))}</p>`
        if (v.you.rerolls > 0 && v.phase !== 'over' && v.phase !== 'round') body += `<button type="button" class="lesson-link game-reroll">${escapeHtml(t('game.you.reroll'))}</button>`
      } else if (v.players >= GAME_MAX_PLAYERS) {
        // AYNI (2026-10-17): meetings hold more people than a game takes; the rest follow the board.
        body += `<p class="lesson-notice">${escapeHtml(t('game.you.full', { n: GAME_MAX_PLAYERS }))}</p>`
      } else {
        body += `<p class="lesson-notice">${escapeHtml(t('game.you.joining'))}</p>`
      }
    }
    const tableGame = isTableKind(v.kind)
    if (v.phase === 'lobby') body += teacher ? teacherLobby(v) : `<p>${escapeHtml(t('game.lobby.wait'))}</p>`
    if (tableGame && v.phase === 'round') {
      if (teacher) body += tablesHtml(v.kind === 'wayra' ? 'wayra' : 'earth', v.tableGames ?? [])
      else {
        const html = v.table ? tableHtml(v.table, status?.gameAt ?? now, now) : null
        if (html) body += html
        else if (v.tableResult) body += `<p class="game-answer">${escapeHtml(t(v.kind === 'wayra' ? 'game.tables.ownWayra' : 'game.tables.ownEarth', { n: v.tableResult.score, stars: v.tableResult.stars }))}</p><p class="lesson-notice">${escapeHtml(t('game.tables.waiting'))}</p>`
        else body += `<p class="lesson-notice">${escapeHtml(t('game.tables.watch'))}</p>`
      }
    }
    if (!tableGame && (v.phase === 'round' || v.phase === 'reveal')) body += roundHtml(v, now)
    if (v.phase === 'reveal' || v.phase === 'over') body += boardHtml(v, v.phase === 'over')
    // The presenter's buttons; a moderator watches.
    let actions = ''
    if (runs) {
      const more = driver ? driver.next < driver.rounds.length : false
      if (v.phase === 'lobby' && tableGame) actions += `<button type="button" class="activity-primary game-start-tables"${v.players ? '' : ' disabled'}>${escapeHtml(t('game.tables.start'))}</button>`
      else if (v.phase === 'lobby') actions += `<button type="button" class="activity-primary game-next"${driver ? '' : ' disabled'}>${escapeHtml(t('game.next.first'))}</button>`
      if (v.phase === 'round' && v.round && !v.round.closed) actions += `<button type="button" class="activity-primary game-close-round">${escapeHtml(t('game.round.close'))}</button>`
      if (v.phase === 'reveal') actions += `<button type="button" class="activity-primary game-next">${escapeHtml(t(more ? 'game.next.round' : 'game.next.final'))}</button>`
      if (v.phase !== 'over') actions += `<button type="button" class="game-end">${escapeHtml(t('game.end'))}</button>`
      else actions += `<button type="button" class="activity-primary game-remove">${escapeHtml(t('game.remove'))}</button>`
      // The game as a Khipu: its rounds and the room's totals (decision G5), to play again as trivia.
      if (v.phase === 'over' && !tableGame && driver?.log?.length) {
        actions += `<button type="button" class="game-khipu-download">${escapeHtml(t('game.khipu.download'))}</button>`
        if (host.canKeep()) actions += `<button type="button" class="game-khipu-save">${escapeHtml(t('game.khipu.save'))}</button>`
        if (khipuNote) body += `<p class="lesson-notice" role="status">${escapeHtml(khipuNote)}</p>`
      }
      if (!driver && !tableGame && v.phase !== 'over') body += `<p class="lesson-notice">${escapeHtml(t('game.driver.lost'))}</p>`
    }
    p.innerHTML = `
      <div class="lesson-head">
        <div class="lesson-head-text">
          <span class="lesson-title">${escapeHtml(v.title)}</span>
          <span class="lesson-progress">${escapeHtml(v.title === kind ? progress : `${kind} · ${progress}`)}</span>
        </div>
      </div>
      <div class="lesson-body game-body" aria-live="polite">${body}</div>
      ${actions ? `<div class="lesson-foot activity-actions game-actions">${actions}</div>` : ''}`
  }

  /** Only the clocks move between the room's messages. */
  function renderTimer(): void {
    const v = status?.game
    if (!v || !panel) return
    const timer = panel.querySelector<HTMLElement>('[data-timer]')
    if (timer) {
      const left = secondsLeft(v, status?.gameAt ?? Date.now())
      timer.textContent = t('game.round.left', { s: left })
      timer.classList.toggle('game-urgent', left <= 5)
    }
    // A table game's clocks, each with its own words.
    for (const el of panel.querySelectorAll<HTMLElement>('[data-deadline]')) {
      const left = Math.max(0, Math.ceil((Number(el.dataset.deadline) - Date.now()) / 1000))
      const tail = el.textContent?.split(' · ')[1]
      el.textContent = t(el.dataset.key as Parameters<typeof t>[0], { s: left }) + (tail ? ` · ${tail}` : '')
      el.classList.toggle('game-urgent', left <= 5)
    }
  }

  // ------------------------------------------------------------ the globe
  function syncGlobe(): void {
    const v = status?.game
    const r = v?.round ?? null
    const id = r ? `${r.id}:${r.closed}:${!!r.result}` : null
    if (r?.id !== shownRound?.split(':')[0]) {
      globe.setAnswers([], null)
      globe.setMine(null)
    }
    shownRound = id
    // A Wayra table: its places as dots, the place found once travelling.
    if (v && isTableKind(v.kind)) {
      const marks = wayraMarks(v.table)
      globe.setAnswers(marks.dots, marks.answer)
      return
    }
    if (!r || r.kind !== 'point') return
    if (r.result) globe.setAnswers(r.result.pins ?? [], r.result.answer)
    globe.setMine(r.pin)
  }

  const offTap = globe.onTap((p) => {
    const v = status?.game
    const r = v?.round
    if (!v || !r || v.teacher || r.kind !== 'point' || r.closed || !r.playing) return
    globe.setMine(p)
    room.send({ t: 'gans', id: r.id, choice: null, p })
  })

  // ------------------------------------------------------------ the presenter's page does its part
  function drive(v: GameView): void {
    if (!driver) return
    if (driver.gameId === null && v.phase === 'lobby' && v.rounds === 0) {
      driver.gameId = v.id
      saveDriver()
    }
    // Another game than the one this tab built (started elsewhere): this tab cannot run it.
    if (driver.gameId !== v.id) {
      driver = null
      saveDriver()
      return
    }
    const r = v.round
    const sent = driver.rounds[driver.next - 1]
    // A closed data round: score the pins from this page's frame, once.
    if (r && r.closed && r.scoring && v.teacher?.toScore && driver.scored !== r.id && sent && sent.spec.kind === 'point' && sent.spec.answer === null) {
      driver.scored = r.id
      saveDriver()
      const data = host.dataProbe()
      const acc = data ? scoreDataPins(data.probe, v.teacher.toScore, sent.high !== false, sampleField(data.probe)) : v.teacher.toScore.map(([pid]) => [pid, 0] as [string, number])
      room.send({ t: 'gscore', id: r.id, acc })
    }
    // A round revealed: kept for the game's Khipu, once.
    if (r && r.result && sent && !(driver.log ?? []).some((x) => x.id === r.id)) {
      driver.log = [...(driver.log ?? []), { id: r.id, spec: sent.spec, datasetId: host.datasetId(), result: r.result }]
      saveDriver()
    }
    // A place round revealed: the globe turns to the answer, and the room with it.
    if (r && r.result?.answer && driver.flown !== r.id) {
      driver.flown = r.id
      saveDriver()
      host.flyTo(r.result.answer)
    }
  }

  // ------------------------------------------------------------ the room
  function render(): void {
    const on = allowed()
    const game = on ? status?.game ?? null : null
    pill.classList.toggle('hidden', !(on && presenter() && !game && !status?.activity && !status?.spotlight))
    if (!(on && presenter() && !game)) openSheet(false)
    else if (sheetOpen) renderSheet()
    renderPanel()
    syncGlobe()
  }

  vrLink = { status: () => status, send: (m) => room.send(m) }
  const offStatus = room.onStatus((next) => {
    if (next.connected && !status?.connected) connectEpoch++
    const had = status?.game ?? null
    status = next
    latestView = next.game
    const v = next.game
    // A participant joins (or comes back) once per connection and game.
    if (v && allowed() && next.seat === 'audience' && !v.you && v.phase !== 'over') {
      const mark = `${v.id}|${connectEpoch}`
      if (joinSent !== mark) {
        joinSent = mark
        room.send({ t: 'gjoin', key: playerKeyFor(opts.code) })
      }
    }
    if (v && presenter()) drive(v)
    // The game went: the Khipu plays as itself again, and this tab forgets the rounds.
    if (had && !v && presenter()) {
      host.setKhipuGame(false)
      driver = null
      saveDriver()
    }
    render()
  })

  const tick = setInterval(renderTimer, 500)
  // At a table game, tell the room this player is still here (a still player would be counted away).
  const touch = setInterval(() => {
    const v = status?.game
    if (!v || !v.table || v.phase !== 'round' || document.visibilityState !== 'visible') return
    room.send({ t: 'gplay', a: { k: 'touch' } })
  }, TOUCH_EVERY_MS)

  return () => {
    clearInterval(tick)
    clearInterval(touch)
    vrLink = null
    offTap()
    offStatus()
    closePanel()
    pill.remove()
    sheet.remove()
    latestView = null
  }
}
